/**
 * SalviaNegocios V55 — Dashboard de Negócios: funil, receita por estado, maturidade
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, DollarSign, MapPin, TrendingUp, Building2 } from "lucide-react";
import { InfoTooltip, IndicatorManualModal } from "@/components/home";

export default function SalviaNegocios() {
  usePageTitle("Dashboard Negócios");
  const { user } = useAuth();

  const { data, isLoading, error } = trpc.dashboardsAdv.salvia.businessDashboard.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Negócios">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title="Dashboard — Negócios">
      <div className="space-y-6">
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={20} className="text-emerald-600" />
                  <span className="text-sm font-medium text-muted-foreground">Receita Estimada Total</span>
                  <InfoTooltip label="" formula="Receita = Σ(vidas) × R$15" description="Proxy de receita por vidas sob gestão" />
                </div>
                <div className="text-3xl font-bold text-emerald-600 tabular-nums">R$ {data.totalRevenue.toLocaleString("pt-BR")}</div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={20} className="text-[#002F59]" />
                  <span className="text-sm font-medium text-muted-foreground">Total de Vidas</span>
                </div>
                <div className="text-3xl font-bold text-[#002F59] tabular-nums">{data.revenueByState.reduce((s: number, r: any) => s + r.totalVidas, 0).toLocaleString("pt-BR")}</div>
              </Card>
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={20} className="text-[#002F59]" />
                  <span className="text-sm font-medium text-muted-foreground">Ticket Médio / Vida</span>
                </div>
                <div className="text-3xl font-bold text-[#002F59]">R$ 15,00</div>
              </Card>
            </div>

            <Card className="p-6">
              <h3 className="text-sm font-semibold text-[#002F59] mb-4">Funil do Ecossistema</h3>
              <div className="space-y-3">
                {data.funnelExpanded.map((step: any, i: number) => {
                  const maxVal = Math.max(...data.funnelExpanded.map((s: any) => s.value), 1);
                  const pct = (step.value / maxVal) * 100;
                  return (
                    <div key={i} className="flex items-center gap-4">
                      <span className="text-sm font-medium w-40 text-right">{step.stage}</span>
                      <div className="flex-1 h-8 bg-slate-100 rounded overflow-hidden relative">
                        <div className="h-full rounded bg-gradient-to-r from-[#002F59] to-[#004080]" style={{ width: `${pct}%` }} />
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white mix-blend-difference">{step.value.toLocaleString("pt-BR")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={18} className="text-[#002F59]" />
                <h3 className="text-sm font-semibold text-[#002F59]">Receita por Estado</h3>
              </div>
              {data.revenueByState.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum dado de estado disponível</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {data.revenueByState.map((s: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="text-sm font-bold">{s.uf || "N/I"}</span>
                        <div className="text-xs text-muted-foreground">{s.totalVidas.toLocaleString("pt-BR")} vidas</div>
                      </div>
                      <span className="text-sm font-semibold text-emerald-600 tabular-nums">R$ {s.receitaEstimada.toLocaleString("pt-BR")}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-6">
              <h3 className="text-sm font-semibold text-[#002F59] mb-4">Maturidade dos Canais</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {Object.entries(data.maturityCounts).map(([level, count]: [string, any], i: number) => {
                  const colors: Record<string, string> = {
                    "Iniciante": "bg-red-100 text-red-700 border-red-200",
                    "Operacional": "bg-amber-100 text-amber-700 border-amber-200",
                    "Estratégico": "bg-emerald-100 text-emerald-700 border-emerald-200",
                  };
                  return (
                    <div key={i} className={`p-4 rounded-lg border ${colors[level] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      <div className="text-3xl font-bold tabular-nums">{count}</div>
                      <div className="text-sm font-medium">{level}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </>
        )}
      </div>
      <IndicatorManualModal profile="salvia" />
    </SalviaLayout>
  );
}
